import { useId, useRef, useState, useCallback } from 'react';

const FOCUSABLE_ELEMENTS_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const usePopover = ({ isOpen, onOpenChange, isFocusOnTrigger = false, focusTrap = false, }) => {
    const generatedId = useId();
    const contentId = `popover-content-${generatedId}`;
    const contentRef = useRef(null);
    const triggerRef = useRef(null);
    const pointerRef = useRef(null);
    // Ref to track if escape key was recently pressed
    const escapeKeyPressedRef = useRef(false);
    const [localOpen, setLocalOpen] = useState(false);
    const actualOpen = isOpen !== undefined ? isOpen : localOpen;
    const handleOpenAutoFocus = useCallback((event) => {
        var _a, _b, _c;
        if (isFocusOnTrigger) {
            event.preventDefault();
            const triggerElement = (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("input, textarea, select");
            if (triggerElement) {
                requestAnimationFrame(() => {
                    triggerElement.focus();
                });
            }
            return;
        }
        event.preventDefault();
        const focusable = (_b = contentRef.current) === null || _b === void 0 ? void 0 : _b.querySelector(FOCUSABLE_ELEMENTS_SELECTOR);
        if (focusable) {
            focusable.focus();
        }
        else {
            (_c = contentRef.current) === null || _c === void 0 ? void 0 : _c.focus();
        }
    }, [isFocusOnTrigger]);
    const handleKeyDownTrigger = useCallback((e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (isOpen === undefined) {
                setLocalOpen((prev) => !prev);
            }
            else {
                onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(!isOpen);
            }
        }
    }, [isOpen, onOpenChange]);
    const getFocusableElementsInContent = useCallback(() => {
        var _a;
        return (_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR);
    }, []);
    const getExternalFocusableElements = useCallback(() => {
        const allElements = document.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR);
        return Array.from(allElements).filter((element) => { var _a; return !((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.contains(element)); });
    }, []);
    const getTriggerElement = useCallback(() => {
        return document.querySelector(`[aria-controls="${contentId}"]`);
    }, [contentId]);
    const handleForwardTab = useCallback((e, lastElement) => {
        if (e.target !== lastElement)
            return false;
        e.preventDefault();
        if (focusTrap) {
            // Focus trap enabled - go back to trigger or first element in content
            const triggerElement = getTriggerElement();
            const focusableElements = getFocusableElementsInContent();
            if (triggerElement) {
                triggerElement.focus();
            }
            else if (focusableElements && focusableElements.length > 0) {
                focusableElements[0].focus();
            }
        }
        else {
            // When focus trap is disabled, move focus to next external element but keep popover open
            const externalElements = getExternalFocusableElements();
            const triggerElement = getTriggerElement();
            if (!triggerElement)
                return true;
            const triggerIndex = externalElements.indexOf(triggerElement);
            const nextElement = externalElements[triggerIndex + 1];
            // Focus next element after trigger, or fallback to first element
            const elementToFocus = nextElement || externalElements[0];
            if (elementToFocus) {
                elementToFocus.focus();
            }
        }
        return true;
    }, [
        getExternalFocusableElements,
        getTriggerElement,
        getFocusableElementsInContent,
        focusTrap,
    ]);
    const handleBackwardTab = useCallback((e, firstElement) => {
        if (e.target !== firstElement)
            return false;
        e.preventDefault();
        if (focusTrap) {
            // When focus trap is enabled, handle the circular focus
            const triggerElement = getTriggerElement();
            const focusableElements = getFocusableElementsInContent();
            // If shift+tab is pressed on the first element inside content
            // focus should go to either the trigger (if available) or last focusable element
            if (triggerElement && !isFocusOnTrigger) {
                // When isFocusOnTrigger is false, we should still focus the trigger on shift+tab
                triggerElement.focus();
            }
            else if (triggerElement && isFocusOnTrigger) {
                // If we have a trigger and we're configured to focus on it, focus the trigger
                triggerElement.focus();
            }
            else if (focusableElements && focusableElements.length > 0) {
                // Otherwise, go to the last element in the popover content (circular navigation)
                const lastElement = focusableElements[focusableElements.length - 1];
                lastElement.focus();
            }
        }
        else {
            // Regular behavior - focus back to trigger
            const triggerElement = getTriggerElement();
            if (triggerElement) {
                triggerElement.focus();
            }
        }
        return true;
    }, [
        getTriggerElement,
        getFocusableElementsInContent,
        focusTrap,
        isFocusOnTrigger,
    ]);
    const handleKeyDownContent = useCallback((e) => {
        if (e.key !== "Tab")
            return;
        const focusableElements = getFocusableElementsInContent();
        if (!focusableElements || focusableElements.length === 0)
            return;
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (e.shiftKey) {
            handleBackwardTab(e, firstElement);
        }
        else {
            handleForwardTab(e, lastElement);
        }
    }, [getFocusableElementsInContent, handleBackwardTab, handleForwardTab]);
    const handleFocusOutside = (event) => {
        event.preventDefault();
    };
    const handlePointerDownOutside = (event) => {
        if (isFocusOnTrigger) {
            const triggerElement = triggerRef.current;
            pointerRef.current = event.target;
            // Allow focus on the trigger element, prevent focus on other elements
            if (triggerElement &&
                (event.target === triggerElement ||
                    triggerElement.contains(event.target))) {
                return; // Allow focus on trigger
            }
            else {
                onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
                if (isOpen === undefined) {
                    setLocalOpen(false);
                }
            }
        }
    };
    const handleEscapeKeyDown = useCallback((e) => {
        // If event is provided, check if it's an Escape key press
        if (e && e.key !== "Escape") {
            return; // Not an escape key event, do nothing
        }
        // Close the popover
        if (isOpen === undefined) {
            setLocalOpen(false);
        }
        else {
            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
        }
        // Get the trigger element
        const triggerElement = getTriggerElement();
        // Only set the escape flag if the trigger is not currently focused
        if (triggerElement && document.activeElement !== triggerElement) {
            escapeKeyPressedRef.current = true;
        }
        // Focus back to the trigger
        if (triggerElement) {
            setTimeout(() => {
                triggerElement.focus();
            }, 0);
        }
    }, [isOpen, onOpenChange, getTriggerElement]);
    const handleTriggerShiftTab = useCallback((e) => {
        if (e.key === "Tab" &&
            e.shiftKey &&
            focusTrap &&
            (isOpen !== undefined ? isOpen : localOpen)) {
            e.preventDefault();
            const focusableElements = getFocusableElementsInContent();
            if (focusableElements && focusableElements.length > 0) {
                // Focus on the last element in the popover content
                const lastElement = focusableElements[focusableElements.length - 1];
                lastElement.focus();
            }
        }
    }, [focusTrap, isOpen, localOpen, getFocusableElementsInContent]);
    const checkActiveElement = () => {
        var _a;
        const actualOpenState = isOpen !== undefined ? isOpen : localOpen;
        if (actualOpenState && isFocusOnTrigger) {
            const triggerElement = (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("input, textarea, select");
            // If the trigger element is the currently active element, don't toggle
            if (triggerElement &&
                (pointerRef.current === triggerElement ||
                    document.activeElement === triggerElement)) {
                return true;
            }
        }
        return false;
    };
    const handleOpenChange = (open) => {
        if (checkActiveElement())
            return;
        // Check if the escape key was recently pressed and we're trying to open
        if (open && escapeKeyPressedRef.current) {
            // Reset the flag but don't open
            escapeKeyPressedRef.current = false;
            return;
        }
        // Always call when changed
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(open);
        if (isOpen === undefined) {
            setLocalOpen(open);
        }
    };
    const handleTriggerClick = () => {
        if (checkActiveElement())
            return;
        // If escape key was just pressed, don't toggle open
        if (escapeKeyPressedRef.current) {
            escapeKeyPressedRef.current = false;
            return;
        }
        if (isOpen === undefined) {
            setLocalOpen((prev) => !prev);
        }
        else {
            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(!isOpen);
        }
    };
    return {
        contentId,
        contentRef,
        triggerRef,
        actualOpen,
        localOpen,
        handleOpenAutoFocus,
        handleKeyDownTrigger,
        handleKeyDownContent,
        handleFocusOutside,
        handlePointerDownOutside,
        handleTriggerShiftTab,
        handleEscapeKeyDown,
        handleOpenChange,
        handleTriggerClick,
    };
};

export { usePopover };
